import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { atsApi } from '../services/atsApi';
import { useAtsNavigationStore } from '../store/atsNavigationStore';
import type { AtsCandidateCard, AtsCandidateListItem, AtsSearchFilters, AtsSearchScope } from '../types';
import {
  ensureSelectedCandidateInList,
  resolveSelectedCandidateId,
  selectedCandidatePin,
} from '../utils/candidateDeepLink';
import { ALL_COUNTRIES_FOLDER, candidateBelongsToFolder } from '../utils/folders';

const LIST_PAGE_SIZE = 50;

const cardListItem = (item: AtsCandidateListItem, card: AtsCandidateCard): AtsCandidateListItem => ({
  ...item,
  full_name: card.full_name,
  email: card.email,
  phone: card.phone,
  location: card.location,
  location_city: card.location_city,
  location_country: card.location_country,
  source: card.source,
  status: card.status,
  is_blacklisted: card.is_blacklisted,
  desired_position: card.desired_position,
  salary_expectation: card.salary_expectation,
  skills: card.skills,
  tags: card.tags,
  labels: card.labels,
  red_flags: card.red_flags,
  documents: card.documents,
  documents_count: card.documents_count,
  has_photo: card.has_photo,
  updated_at: card.updated_at,
});

export const useAtsCandidates = () => {
  const selectedFolder = useAtsNavigationStore((state) => state.selectedFolder);
  const refreshVersion = useAtsNavigationStore((state) => state.refreshVersion);
  const [items, setItems] = useState<AtsCandidateListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<AtsCandidateCard | null>(null);
  const [query, setQuery] = useState('');
  const [searchScope, setSearchScope] = useState<AtsSearchScope>('all');
  const [status, setStatus] = useState('');
  const [searchFilters, setSearchFilters] = useState<AtsSearchFilters>({});
  const [searchAllFolders, setSearchAllFolders] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const selectedCardRef = useRef<AtsCandidateCard | null>(null);
  const folderPathRef = useRef<string | undefined>(undefined);
  const usesSearchRef = useRef(false);
  const listRequestIdRef = useRef(0);
  selectedIdRef.current = selectedId;
  selectedCardRef.current = selectedCard;

  const selectCandidate = useCallback((value: string | null) => {
    selectedIdRef.current = value;
    setSelectedId(value);
  }, []);

  const effectiveFilters = useMemo<AtsSearchFilters>(() => ({
    ...searchFilters,
    ...(status ? { status } : {}),
  }), [searchFilters, status]);
  const usesSearch = Boolean(query.trim()) || Object.keys(effectiveFilters).length > 0;
  usesSearchRef.current = usesSearch;
  const folderPath = useMemo(() => {
    if (searchAllFolders) return undefined;
    const folder = selectedFolder.trim();
    if (!folder || folder === ALL_COUNTRIES_FOLDER) return undefined;
    return folder;
  }, [searchAllFolders, selectedFolder]);
  folderPathRef.current = folderPath;
  const scopedFilters = useMemo<AtsSearchFilters>(() => ({
    ...effectiveFilters,
    ...(folderPath ? { folder_path: folderPath } : {}),
  }), [effectiveFilters, folderPath]);

  const fetchPage = useCallback(async (pageNumber: number) => {
    if (usesSearch) {
      return atsApi.booleanSearch(query, scopedFilters, { page: pageNumber, pageSize: LIST_PAGE_SIZE, searchScope });
    }
    return atsApi.listCandidates({
      page: pageNumber,
      page_size: LIST_PAGE_SIZE,
      folder_path: folderPath,
      status: status || undefined,
    });
  }, [folderPath, query, scopedFilters, searchScope, status, usesSearch]);

  const loadList = useCallback(async () => {
    const requestId = ++listRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPage(1);
      if (requestId !== listRequestIdRef.current) return;
      const pin = selectedCandidatePin(
        selectedIdRef.current,
        selectedCardRef.current,
        folderPathRef.current,
        response.items,
        (item, folder) => candidateBelongsToFolder(item, folder, [item]),
      );
      setPage(response.page);
      setTotal(response.total);
      setHasMore(response.page < response.pages);
      setItems(ensureSelectedCandidateInList(
        response.items.map((item) => (
          selectedCardRef.current?.candidate_id === item.candidate_id
            ? cardListItem(item, selectedCardRef.current)
            : item
        )),
        !usesSearch && pin.pinInList ? selectedIdRef.current : null,
        selectedCardRef.current,
      ));
      setSelectedId((current) => resolveSelectedCandidateId(
        current,
        response.items,
        !usesSearch && pin.keepId,
      ));
    } catch (err) {
      if (requestId === listRequestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Не удалось загрузить кандидатов');
      }
    } finally {
      if (requestId === listRequestIdRef.current) setLoading(false);
    }
  }, [fetchPage, usesSearch]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    const requestId = listRequestIdRef.current;
    setLoadingMore(true);
    setError(null);
    try {
      const response = await fetchPage(page + 1);
      if (requestId !== listRequestIdRef.current) return;
      setItems((current) => {
        const ids = new Set(current.map((item) => item.candidate_id));
        return [...current, ...response.items.filter((item) => !ids.has(item.candidate_id)).map((item) => (
          selectedCardRef.current?.candidate_id === item.candidate_id
            ? cardListItem(item, selectedCardRef.current)
            : item
        ))];
      });
      setPage(response.page);
      setTotal(response.total);
      setHasMore(response.page < response.pages);
    } catch (err) {
      if (requestId === listRequestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Не удалось загрузить следующую страницу');
      }
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, hasMore, loadingMore, page]);

  const loadCard = useCallback(async (candidateId: string) => {
    setCardLoading(true);
    setError(null);
    try {
      const card = await atsApi.getCandidateCard(candidateId);
      if (selectedIdRef.current !== candidateId) {
        return;
      }
      selectedCardRef.current = card;
      setSelectedCard(card);
      setItems((current) => {
        const refreshed = current.map((item) => (
          item.candidate_id === card.candidate_id ? cardListItem(item, card) : item
        ));
        const pin = selectedCandidatePin(
          candidateId,
          card,
          folderPathRef.current,
          refreshed,
          (item, folder) => candidateBelongsToFolder(item, folder, [item]),
        );
        return ensureSelectedCandidateInList(
          refreshed,
          !usesSearchRef.current && pin.pinInList ? candidateId : null,
          card,
        );
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить карточку');
    } finally {
      setCardLoading(false);
    }
  }, []);

  const refreshSelectedCard = useCallback(async () => {
    if (selectedId) {
      await loadCard(selectedId);
    }
  }, [loadCard, selectedId]);

  const applyCard = useCallback((card: AtsCandidateCard) => {
    setSelectedCard(card);
    setItems((current) => current.map((item) => (
      item.candidate_id === card.candidate_id ? cardListItem(item, card) : item
    )));
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList, refreshVersion]);

  useEffect(() => {
    if (selectedId) {
      void loadCard(selectedId);
    } else {
      setSelectedCard(null);
    }
  }, [loadCard, selectedId]);

  return {
    items,
    selectedId,
    setSelectedId: selectCandidate,
    selectedCard,
    setSelectedCard: applyCard,
    query,
    setQuery,
    searchScope,
    setSearchScope,
    status,
    setStatus,
    searchFilters,
    setSearchFilters,
    searchAllFolders,
    setSearchAllFolders,
    loading,
    loadingMore,
    hasMore,
    total,
    cardLoading,
    error,
    reload: loadList,
    loadMore,
    refreshSelectedCard,
  };
};
